import { motion } from "framer-motion";
import Item from "./Item";
import { useAppSelector, useAppDispatch } from "hooks";
import { logout } from "../../../redux/slices/auth.slice";
import { TesLogout } from "components/icons";
import { Button } from "components/widgets";
import { CONTACT, LOGIN, SIGNUP, ABOUT_ME, HOME } from "routes/CONSTANTS";
import { toast } from "react-toastify";

interface Props {
  open: boolean;
}

const variants = {
  open: {
    transition: {
      staggerChildren: 0.07,
      staggerDirection: 1,
      delayChildren: 0.2
    }
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 }
  }
};

const items = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 }
    }
  }
};

const Drawer = ({ open }: Props) => {
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    try {
      void dispatch(logout());
      toast.success("Logged out successfully");
    } catch (error: any) {
      toast.error(error);
    }
  };

  return (
    <motion.aside
      initial={{ height: 0 }}
      animate={{ height: "100vh" }}
      exit={{ height: 0, transition: { delay: 0.7, duration: 0.3 } }}
      className={`${
        open ? "block lg:hidden" : "hidden"
      } fixed w-full top-16 left-0 bottom-0 px-6 bg-gradient-to-br from-gray-900 via-black to-green-950 backdrop-blur-xl text-white z-30 overflow-y-auto border-t-2 border-yellow-500/30`}
    >
      {/* Decorative Glow */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-yellow-500/10 to-transparent pointer-events-none" />

      <motion.ul
        initial="closed"
        animate="open"
        exit="closed"
        variants={variants}
        className="py-4 w-full h-full flex flex-col justify-between items-center relative z-10"
      >
        {/* Navigation Items */}
        <motion.div
          variants={items}
          className="w-full flex flex-col justify-center items-center text-xl gap-2"
        >
          {isLoggedIn && (
            <>
              <motion.div variants={items} className="w-full">
                <Item to={HOME}>
                  <span className="flex items-center gap-3">
                    <span className="text-2xl">🏠</span>
                    <span className="text-yellow-400 font-semibold">Dashboard</span>
                  </span>
                </Item>
              </motion.div>
              <motion.div variants={items} className="w-full">
                <Item to={ABOUT_ME}>
                  <span className="flex items-center gap-3">
                    <span className="text-2xl">👤</span>
                    <span className="text-yellow-400 font-semibold">My Profile</span>
                  </span>
                </Item>
              </motion.div>
              <motion.div variants={items} className="w-full my-2">
                <div className="border-t border-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
              </motion.div>
            </>
          )}

          <motion.div variants={items} className="w-full">
            <Item to={CONTACT}>
              <span className="flex items-center gap-3">
                <span className="text-2xl">📧</span>
                <span className="text-white font-medium">Contact Us</span>
              </span>
            </Item>
          </motion.div>

          <motion.div variants={items} className="w-full">
            <Item to="#">
              <span className="flex items-center gap-3">
                <span className="text-2xl">🛠️</span>
                <span className="text-white font-medium">Services</span>
              </span>
            </Item>
          </motion.div>

          <motion.div variants={items} className="w-full">
            <Item to="#">
              <span className="flex items-center gap-3">
                <span className="text-2xl">ℹ️</span>
                <span className="text-white font-medium">About Us</span>
              </span>
            </Item>
          </motion.div>
        </motion.div>

        {/* Auth Buttons */}
        <motion.div variants={items} className="w-full px-2 pb-16">
          {!isLoggedIn ? (
            <div className="w-full flex flex-col gap-4">
              <Button
                to={LOGIN}
                variant="outline"
                className="w-full py-4 text-lg border-2 bg-blue-400 border-yellow-500 text-white-400 hover:bg-white-500 hover:text-black rounded-xl transition-all font-bold shadow-[0_0_20px_rgba(234,179,8,0.3)] hover:shadow-[0_0_30px_rgba(234,179,8,0.6)]"
              >
                Sign In
              </Button>
              <Button
                to={SIGNUP}
                className="w-full py-4 text-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-black hover:from-yellow-600 hover:to-yellow-700 rounded-xl transition-all font-bold shadow-[0_4px_20px_rgba(234,179,8,0.5)]"
              >
                Create an Account
              </Button>
            </div>
          ) : (
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleLogout}
              className="flex items-center justify-center py-4 w-full gap-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl transition-all shadow-[0_4px_20px_rgba(220,38,38,0.5)] hover:shadow-[0_4px_30px_rgba(220,38,38,0.7)] text-white font-bold border-2 border-red-500/50"
            >
              <TesLogout size={24} className="text-white" />
              <p className="text-lg">Logout</p>
            </motion.button>
          )}
        </motion.div>
      </motion.ul>

      {/* Bottom Decorative Glow */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-green-900/20 to-transparent pointer-events-none" />
    </motion.aside>
  );
};

export default Drawer;
